import { ExperimentClient } from '@amplitude/experiment-js-client';
import * as amplitude from '@amplitude/analytics-browser';
import Config from '../Config';

const config = new Config();

export const HUB_TRANSFERS_REDIRECT = 'aop-hub-transfers-redirect';
export const REMOVE_API_SCOPES = 'aop-remove-api-scopes';
export const ENTITY_FORMSTACK_EMBED = 'aop-entity-formstack-embed';
export const HIDE_PROMO_CODE = 'aop-hide-promo-code';

let experiment;
let ready;
let preAuth;
let preAuthReady;
let deviceIdWait;
let identifiedFdcnId = null;
let variantsResolved = false;
const decisions = {};

// Routes Experiment's $exposure events into the same Amplitude Analytics
// instance App initializes, so treatment can be compared against control.
// Without a provider ExperimentClient.exposure() has nowhere to send the event
// and the experiment cannot be measured.
const exposureTrackingProvider = {
  track: exposure => amplitude.track('$exposure', exposure),
};

// Experiment resolves identity from device_id and user_id. Reusing the device id
// Analytics already generated keeps both SDKs on one identity, so an $exposure
// recorded before registration still joins the applicant's funnel events.
const buildUser = (fdcnId) => {
  const user = {};
  const deviceId = amplitude.getDeviceId();
  if (deviceId) user.device_id = deviceId;
  if (fdcnId) user.user_id = fdcnId;
  return user;
};

const onVariantsResolved = () => { variantsResolved = true; };

// Evaluates anonymously on the first call so flags work before registration,
// then upgrades to the identified applicant when the fdcnId first appears. App
// calls this on every field change, so re-identification is guarded to one
// refetch per applicant rather than one per keystroke.
const initialize = (fdcnId) => {
  if (!config.amplitudeDeploymentKey) return;

  if (!experiment) {
    // automaticExposureTracking is off so variant() reads stay side-effect free;
    // exposure is reported explicitly via trackFeatureFlag when a flag is acted on.
    //
    // The fetch is bounded and not retried because resolveFeatureFlag waits on it
    // behind a click: on the SDK defaults a stalled network would leave the button
    // doing nothing for ten seconds instead of falling through to its off path.
    experiment = new ExperimentClient(config.amplitudeDeploymentKey, {
      automaticExposureTracking: false,
      exposureTrackingProvider,
      fetchTimeoutMillis: 3000,
      retryFetchOnFailure: false,
    });
    identifiedFdcnId = fdcnId || null;
    // The user goes to start(), not setUser: start() passes its own argument through to
    // setUser, so a bare start() would replace the user with null and fetch variants for
    // nobody -- every flag then reads as off, and no targeting or allowlist can match.
    //
    // A failed fetch also leaves every flag unresolved, which reads as off.
    ready = experiment.start(buildUser(fdcnId)).then(onVariantsResolved).catch(() => {});
    return;
  }

  if (fdcnId && fdcnId !== identifiedFdcnId) {
    identifiedFdcnId = fdcnId;
    experiment.setUser(buildUser(fdcnId));
    ready = experiment.fetch().then(onVariantsResolved).catch(() => {});
  }
};

// A flag's value is frozen the first time it is read, so re-identifying the
// applicant mid-wizard cannot flip a decision the UI has already acted on.
const getFeatureFlag = (flagKey) => {
  if (!flagKey || !experiment) return false;
  if (Object.prototype.hasOwnProperty.call(decisions, flagKey)) return decisions[flagKey];
  // Before the fetch lands every flag reads as an empty variant. Caching that
  // would pin the flag off for the whole session, so nothing is frozen until
  // there is a real answer to freeze.
  if (!variantsResolved) return false;

  decisions[flagKey] = experiment.variant(flagKey).value === 'on';
  return decisions[flagKey];
};

const trackFeatureFlag = (flagKey) => {
  if (!flagKey || !experiment) return;
  experiment.exposure(flagKey);
};

// getFeatureFlag reads whatever the client has cached, which is nothing until
// start()'s fetch lands -- and an unresolved variant reads as off, so a flag
// acted on right after a page loads looks simply disabled. This resolves the
// flag instead: it identifies the applicant if App has not yet (a reload
// re-fetches the registration, so fdcnId can arrive after the page is
// interactive), waits for the fetch, then reports the exposure and the value.
const resolveFeatureFlag = (flagKey, fdcnId) => {
  initialize(fdcnId);
  if (!flagKey || !experiment) return Promise.resolve(false);

  return ready.then(() => {
    const isOn = getFeatureFlag(flagKey);
    trackFeatureFlag(flagKey);
    return isOn;
  }).catch(() => false);
};

// Analytics sets its device id only once init() finishes, and init() waits on a
// remote-config fetch first. App calls init() in the same task as the first
// render, so a page that reads a flag on mount after a fresh load finds no id
// yet. Waiting for it -- bounded like the variant fetch -- keeps a reload of that
// page from reading every flag as off.
//
// The wait is shared and paid at most once per page: the registration page reads
// a flag on mount and auth0Login reads another on submit, in front of the Auth0
// redirect, so a second full wait there would stall sign-up. An id that turns up
// after the wait gave up is still picked up by the next read.
const waitForDeviceId = () => {
  const deviceId = amplitude.getDeviceId();
  if (deviceId) return Promise.resolve(deviceId);

  if (!deviceIdWait) {
    deviceIdWait = new Promise((resolve) => {
      const deadline = Date.now() + 3000;
      const check = () => {
        const id = amplitude.getDeviceId();
        if (id || Date.now() >= deadline) {
          resolve(id);
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }
  return deviceIdWait;
};

// Registration reads a flag before the backend has issued an fdcnId, so there is
// no user to key on. That read gets its own device-scoped client, kept separate
// from the identified one above so it cannot change how a flag buckets -- or
// when its variants are fetched -- for an applicant already in the wizard.
//
// This sits in front of the Auth0 call, so the fetch is bounded and not retried:
// registration must not stall behind Amplitude. When Amplitude cannot answer --
// no device id in time, no deployment key, a timeout, a failed fetch -- the
// caller's fallback is used. A fetch that lands decides on its own: anything but
// "on" resolves false.
const resolvePreAuthFlag = (flagKey, fallback = false) => {
  if (!flagKey || !config.amplitudeDeploymentKey) return Promise.resolve(fallback);

  return waitForDeviceId().then((deviceId) => {
    if (!deviceId) return fallback;

    if (!preAuth) {
      // throwOnError makes a failed fetch reject start(); otherwise it resolves
      // and reads exactly like a flag that is off.
      preAuth = new ExperimentClient(config.amplitudeDeploymentKey, {
        automaticExposureTracking: false,
        exposureTrackingProvider,
        fetchTimeoutMillis: 3000,
        retryFetchOnFailure: false,
        pollOnStart: false,
        throwOnError: true,
      });
      preAuthReady = preAuth.start({ device_id: deviceId }).then(() => true, () => false);
    }

    return preAuthReady.then((fetched) => {
      if (!fetched) return fallback;
      const isOn = preAuth.variant(flagKey).value === 'on';
      preAuth.exposure(flagKey);
      return isOn;
    });
  }).catch(() => fallback);
};

const AmplitudeFlags = {
  initialize,
  getFeatureFlag,
  trackFeatureFlag,
  resolveFeatureFlag,
  resolvePreAuthFlag,
};

export default AmplitudeFlags;
